import { Box, Grid } from '@mui/material';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import DualYAxisLineChartWidget from 'components/Widgets/DualYAxisLineChartWidget';
import React, { useEffect, useState } from 'react';
import { fetchHappySignalTrend } from 'service/service';
import CaptureExportControls from 'utils/CaptureExportControls';

function HappySignalByTrend() {
	const [isLoading, setIsLoading] = React.useState(false);
	const [trendData, setTrendData] = useState<any>({});
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);;
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [sortingInfo] = useState({ sortBy: 'size', sortDir: 'desc' });
	const [tableDataList, setTableDataList] = useState<any[]>([]);
	const [isExporting, setIsExporting] = useState(false);

	const defaultColumnNames = ['Month', 'Happiness Index', 'Lost Time (Hours)', 'Responses'];

	const handleSorting = (newSortingInfo: any) => {
	};

	const handlepageSizeChange = (event: any) => {
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
	};

	const getColumns = () => {
		const columnMap: Array<{ key: string; label: string }> = [
			{ key: 'month', label: 'Month' },
			{ key: 'happinessIndex', label: 'Happiness Index' },
			{ key: 'lostTime', label: 'Lost Time (Hours)' },
			{ key: 'responses', label: 'Responses' }
		];

		const percentFields: string[] = []; // Add keys here if needed in the future
		const hourFields: string[] = ['lostTime'];

		return columnMap.map((col) => {
			let label = col.label;
			return {
				key: col.key,
				name: label,
				resizable: true,
				formatter: ({ row }: any) => {
					let value = row[col.key];
					if (percentFields.includes(col.key)) {
						value = `${value}%`;
					} else if (hourFields.includes(col.key)) {
						value = `${value} Hrs`;
					}
					return <RowTypography>{value}</RowTypography>;
				}
			};
		});
	};

	useEffect(() => {
		fetchAndPopulateData();
	}, []);

	const fetchAndPopulateData = async () => {
		try {
			setIsLoading(true);
			const response = await fetchHappySignalTrend();
			if (response.success && response.data) {
				const { correlation, monthlySummary, page } = response.data;

				// Dual Y-axis chart data normalization
				const xAxisLabels = correlation.map((item: any) => item.month);
				const happinessValues = correlation.map((item: any) => item.happinessIndex);
				const lostTimeValues = correlation.map((item: any) => item.lostTime);

				setTrendData({
					xAxisLabels,
					series: [
						{
							name: 'Happiness Index',
							values: happinessValues,
							yAxis: 'left',
							color: '#ff00b3'
						},
						{
							name: 'Lost Time (Hours)',
							values: lostTimeValues,
							yAxis: 'right',
							color: '#ffa726'
						}
					]
				});

				//Table data normalization
				const tableData = monthlySummary.map((item: any) => ({
					month: item.month,
					happinessIndex: item.happinessIndex,
					lostTime: item.lostTime,
					responses: item.responses
				}));

				setTableDataList(tableData);
				setPaginationData({
					totalElements: page.totalElements,
					totalPages: page.totalPages
				});
				setPage(page.currentPage);
				setPageSize(page.size);
			}
		} catch (error) {
			console.error('Error fetching trend data:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleExportToExcel = () => {
		if (isExporting) return;
		setIsExporting(true);
		try {
			const storedPref = localStorage.getItem('Monthly_Performance_Summary_user_col_pref');
			const storedColumnNames: string[] | null = storedPref ? JSON.parse(storedPref) : null;
			const preferredColumnNames: string[] = storedColumnNames?.length
				? storedColumnNames
				: defaultColumnNames;

			const allColumns = getColumns();
			const nameToKey: Record<string, string> = {};
			allColumns.forEach((col: any) => { nameToKey[col.name] = col.key; });

			const exportData = tableDataList.map((row: any) => {
				const exportRow: Record<string, any> = {};
				preferredColumnNames.forEach((colName: string) => {
					const key = nameToKey[colName];
					if (key !== undefined) {
						const val = row[key];
						exportRow[colName] = val ?? '';
					}
				});
				return exportRow;
			});

			const escapeCsv = (v: any) => `"${String(v ?? '').replaceAll('"', '""')}"`;
			const header = preferredColumnNames.filter(n => nameToKey[n] !== undefined).join(',');
			const rows = exportData.map((r: any) =>
				preferredColumnNames
					.filter((n: string) => nameToKey[n] !== undefined)
					.map((n: string) => escapeCsv(r[n]))
					.join(',')
			);
			const csv = [header, ...rows].join('\n');
			const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `HappySignal_Trends.csv`;
			document.body.appendChild(a);
			a.click();
            a.remove();
			URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Export failed:', error);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<Box>
			<Grid container spacing={2}>
				<Grid item xs={12} sx={{display:'flex'}}>
					
						<DualYAxisLineChartWidget title="Happiness Index vs Lost Time Correlation" isLoading={isLoading} data={trendData} sx={{ flexGrow: '1'}} />
							<CaptureExportControls
								onScreenshot={() => {}}
								screenCapturing={false}
								onExport={handleExportToExcel}
								isExporting={isExporting}
								isVisible={true}
								showExport={true}
								type="snow"
								exportLabel="Export to CSV"
								sx={{ ml: 0, '& #exclude-camera': { display: 'none' } }}
							/>
				</Grid>
				<Grid item xs={12}>
					<CustomizedDataGrid
						title="Monthly Performance Summary"
						tableType="Monthly_Performance_Summary"
						tableColumns={getColumns()}
						tableData={tableDataList}
						defaultColumnNames={defaultColumnNames}
						onSorting={handleSorting}
						currSorting={sortingInfo}
						page={page}
						pageSize={pageSize}
						paginationData={paginationData}
						listLength={tableDataList?.length}
						handlepageSizeChange={handlepageSizeChange}
						handlePageChange={handlePageChange}
						// showGoToPage={true}
						isDateRangeFilter={false}
						isDisabled={false}
						isFetching={isLoading}
						// autoSetDraggedColumns={false}
						childRowClass={true}
						isShowMoreButton={true}
						isSortIcon={true}
						showExport={false}
						isLighterTheme={true}
					/>
				</Grid>
			</Grid>
		</Box>
	);
}

export default HappySignalByTrend;
